import React, {ReactNode} from 'react';
import Alert from '@mui/joy/Alert';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Divider from '@mui/joy/Divider';
import FormControl from '@mui/joy/FormControl';
import FormHelperText from '@mui/joy/FormHelperText';
import FormLabel from '@mui/joy/FormLabel';
import Modal from '@mui/joy/Modal';
import ModalClose from '@mui/joy/ModalClose';
import ModalDialog from '@mui/joy/ModalDialog';
import Stack from '@mui/joy/Stack';
import Textarea from '@mui/joy/Textarea';
import Typography from '@mui/joy/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {NotificationService} from '../../common/notification';
import {AdminCriticalActionPreview} from '../../models/externalMarkets';

/** Короче не объяснить, зачем меняешь заказ: столько же требует сервер */
export const MinReasonLength = 10;

type ActionResult = { result: boolean; value?: AdminCriticalActionPreview; error?: string };

type Props = {
    open: boolean;
    onClose: () => void;
    /** Название действия, например «Привязка Steam-офера» */
    title: string;
    /** Зачем это действие и когда его применять — показывается на первом шаге */
    description: ReactNode;
    /** Поля действия (первый шаг) */
    form: ReactNode;
    /** Поля заполнены корректно — можно смотреть предпросмотр */
    formValid: boolean;
    /** Предпросмотр сервера: ничего не меняет */
    loadPreview: () => Promise<ActionResult>;
    /** Выполнение с причиной: сервер заново проверяет предусловия */
    execute: (reason: string) => Promise<ActionResult>;
    /** Текст кнопки выполнения: что именно произойдёт, например «Привязать Steam-офер #789» */
    confirmLabel: string;
    /** Действие выполнено — перечитать данные */
    onDone: () => void;
};

/**
 * Критическое действие по заказу в два шага: параметры → подтверждение. На втором шаге — предпросмотр сервера
 * (что произойдёт, зачем, последствия, предупреждения), обязательная причина и кнопка с названием действия.
 * Пока предпросмотр не получен, причина не указана или сервер запрещает действие, выполнить его нельзя.
 */
export function CriticalActionDialog(props: Props) {
    const {open, onClose, title, description, form, formValid, loadPreview, execute, confirmLabel, onDone} = props;
    const [step, setStep] = React.useState<'form' | 'confirm'>('form');
    const [preview, setPreview] = React.useState<AdminCriticalActionPreview>();
    const [reason, setReason] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string>();
    const [notice, setNotice] = React.useState<string>();

    React.useEffect(() => {
        if (open) {
            setStep('form');
            setPreview(undefined);
            setReason('');
            setError(undefined);
            setNotice(undefined);
        }
    }, [open]);

    const close = () => {
        if (!busy) {
            onClose();
        }
    };

    const showPreview = async () => {
        setBusy(true);
        setError(undefined);
        setNotice(undefined);
        try {
            const res = await loadPreview();
            if (res.result && res.value) {
                setPreview(res.value);
                setStep('confirm');
            } else {
                setError(res.error ?? 'Не удалось получить предпросмотр действия');
            }
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        setBusy(true);
        setError(undefined);
        setNotice(undefined);
        try {
            const res = await execute(reason.trim());
            if (!res.result || !res.value) {
                setError(res.error ?? 'Действие не выполнено');
                return;
            }
            if (!res.value.executed) {
                // Пока читали предпросмотр, состояние изменилось: показываем актуальный запрет
                setPreview(res.value);
                setNotice('Действие не выполнено: состояние заказа изменилось. Ниже — актуальная причина.');
                return;
            }
            NotificationService.alertSuccess(res.value.whatWillHappen, title);
            onDone();
            onClose();
        } finally {
            setBusy(false);
        }
    };

    const reasonOk = reason.trim().length >= MinReasonLength;
    const canConfirm = !!preview?.canExecute && reasonOk && !busy;

    return (
        <Modal open={open} onClose={close}>
            <ModalDialog sx={{width: 640, maxWidth: '95vw', overflowY: 'auto'}}>
                <ModalClose disabled={busy}/>
                <Typography level="h4">{title}</Typography>
                <Typography level="body-xs" color="neutral">
                    Шаг {step === 'form' ? '1 из 2: параметры' : '2 из 2: подтверждение'}
                </Typography>
                <Divider/>

                {step === 'form' &&
                    <Stack spacing={1.5}>
                        <Typography level="body-sm">{description}</Typography>
                        {form}
                        {error && <Alert color="danger" variant="soft">{error}</Alert>}
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button variant="plain" color="neutral" onClick={close} disabled={busy}>Отмена</Button>
                            <Button onClick={showPreview} disabled={!formValid || busy}
                                    startDecorator={busy ? <CircularProgress size="sm"/> : undefined}>
                                Далее: что произойдёт
                            </Button>
                        </Stack>
                    </Stack>}

                {step === 'confirm' && preview &&
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography level="title-md">{preview.title}</Typography>
                            {preview.irreversible &&
                                <Chip size="sm" color="danger" variant="solid">Необратимо</Chip>}
                        </Stack>

                        {!preview.canExecute &&
                            <Alert color="danger" variant="soft" startDecorator={<WarningAmberIcon/>}>
                                Сейчас выполнить нельзя: {preview.blockReason}
                            </Alert>}
                        {notice && <Alert color="warning" variant="soft">{notice}</Alert>}

                        {preview.canExecute && <>
                            <Section title="Что произойдёт">{preview.whatWillHappen}</Section>
                            <Section title="Зачем">{preview.why}</Section>
                            {preview.consequences.length > 0 &&
                                <Section title="Последствия">
                                    <ul style={{margin: 0, paddingLeft: 18}}>
                                        {preview.consequences.map((c) => <li key={c}>{c}</li>)}
                                    </ul>
                                </Section>}
                            {preview.warnings.length > 0 &&
                                <Alert color="warning" variant="soft" startDecorator={<WarningAmberIcon/>}>
                                    <Stack spacing={0.5}>
                                        {preview.warnings.map((w) => <Typography key={w} level="body-sm">{w}</Typography>)}
                                    </Stack>
                                </Alert>}

                            <FormControl required error={reason.length > 0 && !reasonOk}>
                                <FormLabel>Причина — зачем вы это делаете</FormLabel>
                                <Textarea minRows={2} value={reason} disabled={busy}
                                          placeholder="Например: сверил номер Steam-офера по трейду в истории аккаунта поставщика"
                                          onChange={(e) => setReason(e.target.value)}/>
                                <FormHelperText>
                                    Попадёт в журнал изменений поставщика. Не короче {MinReasonLength} символов
                                    ({reason.trim().length}/{MinReasonLength}).
                                </FormHelperText>
                            </FormControl>
                        </>}

                        {error && <Alert color="danger" variant="soft">{error}</Alert>}

                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button variant="plain" color="neutral" onClick={() => setStep('form')} disabled={busy}>
                                Назад
                            </Button>
                            <Button variant="plain" color="neutral" onClick={close} disabled={busy}>Отмена</Button>
                            {preview.canExecute &&
                                <Button color={preview.irreversible ? 'danger' : 'primary'} onClick={confirm}
                                        disabled={!canConfirm}
                                        startDecorator={busy ? <CircularProgress size="sm"/> : undefined}>
                                    {confirmLabel}
                                </Button>}
                        </Stack>
                    </Stack>}
            </ModalDialog>
        </Modal>
    );
}

function Section(props: { title: string; children: ReactNode }) {
    return (
        <Stack spacing={0.25}>
            <Typography level="title-sm">{props.title}</Typography>
            <Typography level="body-sm" component="div">{props.children}</Typography>
        </Stack>
    );
}
